const STORAGE_KEY = "cc_utm_params";

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

export function captureUtmParamsFromSearch(search: string): UtmParams {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(normalized);
  const utm: UtmParams = {};

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });

  return utm;
}

export function getStoredUtmParams(): UtmParams {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const utm: UtmParams = {};
    UTM_KEYS.forEach((key) => {
      if (typeof parsed[key] === "string" && parsed[key]) {
        utm[key] = parsed[key];
      }
    });
    return utm;
  } catch {
    return {};
  }
}

export function persistUtmParams(search: string = window.location.search): UtmParams {
  const fromUrl = captureUtmParamsFromSearch(search);
  if (Object.keys(fromUrl).length > 0) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  }
  return getStoredUtmParams();
}

export function getUtmParams(search: string = window.location.search): UtmParams {
  return { ...getStoredUtmParams(), ...captureUtmParamsFromSearch(search) };
}

export function buildSearchWithStoredUtm(
  currentSearch: string,
  stored: UtmParams = getStoredUtmParams(),
): string {
  const params = new URLSearchParams(
    currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch,
  );
  Object.entries(stored).forEach(([key, value]) => {
    if (value && !params.get(key)) {
      params.set(key, value);
    }
  });
  return params.toString();
}

export function restoreUtmParamsToUrl(): void {
  if (typeof window === "undefined") return;

  const stored = persistUtmParams();
  if (Object.keys(stored).length === 0) return;

  const nextSearch = buildSearchWithStoredUtm(window.location.search, stored);
  const currentSearch = window.location.search.startsWith("?")
    ? window.location.search.slice(1)
    : window.location.search;

  if (!nextSearch || nextSearch === currentSearch) return;

  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}?${nextSearch}${window.location.hash}`,
  );
}

export function v2EnrollmentHomePath(search: string): string {
  const query = search || "";
  return `/v2/enrollment/${query.startsWith("?") || query === "" ? query : `?${query}`}`;
}
